import { useCallback, useEffect, useRef, useState } from "react";
import type { ColumnLevel, PathSegment } from "./types";

type ColumnItemCounts = Map<number, number>;

export function useFileBrowserNav(
  initialPath: PathSegment[],
  onClose: () => void,
  active: boolean,
  onTogglePreview?: () => void
) {
  const [path, setPath] = useState<PathSegment[]>(initialPath);
  const [focusedColumnIndex, setFocusedColumnIndex] = useState(
    initialPath.length
  );
  const [focusedRowIndex, setFocusedRowIndex] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const columnItemCounts = useRef<ColumnItemCounts>(new Map());
  const columnSelectedRows = useRef<Map<number, number>>(new Map());

  const registerColumnItemCount = useCallback(
    (columnIndex: number, count: number) => {
      columnItemCounts.current.set(columnIndex, count);
    },
    []
  );

  const replaceItem = useCallback(
    (
      columnIndex: number,
      level: ColumnLevel,
      id: string,
      label: string,
      meta?: Record<string, string>
    ) => {
      setPath((prev) => {
        const next = prev.slice(0, columnIndex);
        next.push({ level, id, label, meta });
        return next;
      });
    },
    []
  );

  const selectItem = useCallback(
    (
      columnIndex: number,
      level: ColumnLevel,
      id: string,
      label: string,
      rowIndex: number,
      meta?: Record<string, string>
    ) => {
      replaceItem(columnIndex, level, id, label, meta);
      columnSelectedRows.current.set(columnIndex, rowIndex);
      setFocusedColumnIndex(columnIndex);
      setFocusedRowIndex(rowIndex);
    },
    [replaceItem]
  );

  const prevPathLength = useRef(path.length);
  useEffect(() => {
    if (path.length <= prevPathLength.current) {
      prevPathLength.current = path.length;
      return;
    }
    prevPathLength.current = path.length;
    const el = scrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ left: el.scrollWidth, behavior: "smooth" });
    });
  });

  useEffect(() => {
    if (!active) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
        case "ArrowUp": {
          e.preventDefault();
          e.stopPropagation();
          const count = columnItemCounts.current.get(focusedColumnIndex) ?? 0;
          if (count === 0) return;
          const nextRow =
            e.key === "ArrowDown"
              ? focusedRowIndex < count - 1
                ? focusedRowIndex + 1
                : 0
              : focusedRowIndex > 0
                ? focusedRowIndex - 1
                : count - 1;
          setFocusedRowIndex(nextRow);
          columnSelectedRows.current.set(focusedColumnIndex, nextRow);
          break;
        }
        case "ArrowRight":
        case "Enter": {
          e.preventDefault();
          e.stopPropagation();
          const nextCol = focusedColumnIndex + 1;
          if ((columnItemCounts.current.get(nextCol) ?? 0) > 0) {
            setFocusedColumnIndex(nextCol);
            setFocusedRowIndex(0);
          }
          break;
        }
        case "ArrowLeft": {
          e.preventDefault();
          e.stopPropagation();
          if (focusedColumnIndex > 0) {
            const targetCol = focusedColumnIndex - 1;
            setFocusedColumnIndex(targetCol);
            setFocusedRowIndex(columnSelectedRows.current.get(targetCol) ?? 0);
            setPath((prev) => prev.slice(0, targetCol));
          }
          break;
        }
        case "Escape": {
          e.stopPropagation();
          onClose();
          break;
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [active, focusedColumnIndex, focusedRowIndex, onClose]);

  useEffect(() => {
    if (!active) return;
    if (!onTogglePreview) return;
    const handleSpace = (e: KeyboardEvent) => {
      if (e.key !== " ") return;
      e.preventDefault();
      e.stopPropagation();
      onTogglePreview();
    };
    document.addEventListener("keydown", handleSpace, true);
    return () => document.removeEventListener("keydown", handleSpace, true);
  }, [active, onTogglePreview]);

  return {
    path,
    focusedColumnIndex,
    focusedRowIndex,
    scrollRef,
    selectItem,
    replaceItem,
    registerColumnItemCount,
    setFocusedColumnIndex,
    setFocusedRowIndex,
  };
}
